import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { defer, map, Observable, of, throwError } from 'rxjs';
import {
  WorkflowItem,
  WorkflowComment,
  WorkflowDecisionAction,
  Facility,
} from '#models';
import { environment } from '#environments';
import { AuthService } from '../auth/auth.service';
import { CaseService } from './case.service';

interface FacilityDto {
  facilityId: number;
  facilityName: string | null;
}

interface WorkflowCommentDto {
  agentCaseCommentId: number;
  commentText: string;
  createdBy: string;
  createdByEmail: string | null;
  createdDate: string | null;
}

@Injectable({ providedIn: 'root' })
export class WorkflowService {
  private _http = inject(HttpClient);
  private _authService = inject(AuthService);
  private _caseService = inject(CaseService);

  getAssets(): Observable<string[]> {
    if (environment.useMockData) {
      return defer(() => import('../../features/workflow-inbox/data/workflow.mock'))
        .pipe(map(data => data.WORKFLOW_ASSETS_MOCK));
    }
    return this._http.get<string[]>(`${environment.apiUrl}/asset`);
  }

  getFacilities(): Observable<Facility[]> {
    if (environment.useMockData) {
      return defer(() => import('../../features/workflow-inbox/data/workflow.mock'))
        .pipe(map(data => data.WORKFLOW_FACILITIES_MOCK));
    }
    return this._http
      .get<FacilityDto[]>(`${environment.apiUrl}/facility`)
      .pipe(map(dtos => dtos.map(dto => ({ id: dto.facilityId.toString(), name: dto.facilityName ?? '' }))));
  }

  searchWorkflows(asset: string | null, facilityId: string | null): Observable<WorkflowItem[]> {
    void asset;
    void facilityId;
    if (environment.useMockData) {
      return defer(() => import('../../features/workflow-inbox/data/workflow.mock'))
        .pipe(map(data => data.WORKFLOW_ITEMS_MOCK));
    }
    return of([]);
  }

  getItemDetail(workflowId: string): Observable<WorkflowItem> {
    if (/^case-[1-9]\d*$/.test(workflowId)) {
      return this._caseService.getDetail(Number(workflowId.slice(5)));
    }
    if (environment.useMockData) {
      return defer(() => import('../../features/workflow-inbox/data/workflow.mock'))
        .pipe(map(data => data.WORKFLOW_ITEMS_MOCK.find(i => i.id === workflowId) ?? data.WORKFLOW_ITEMS_MOCK[0]));
    }
    return throwError(() => new Error(`Unsupported inbox item id: ${workflowId}`));
  }

  /**
   * Mock-mode Approve/Reject decision. Returns null so the caller keeps its optimistic update;
   * outside mock mode decisions are owned by the workflow engine.
   */
  recordDecision(workflowId: string, action: WorkflowDecisionAction, agentFeedback?: string): Observable<WorkflowItem | null> {
    void workflowId;
    void action;
    void agentFeedback;
    if (environment.useMockData) {
      return of(null);
    }
    return throwError(() => new Error('Decisions are owned by the workflow engine.'));
  }

  submitFeedback(workflowId: string, thumbs: 'up' | 'down', reasons: string[] = [], comment = ''): Observable<void> {
    if (environment.useMockData) return of(undefined);
    const body: { thumbs: 'up' | 'down'; reasons?: string[]; comment?: string } = { thumbs };
    if (reasons.length) {
      body.reasons = reasons;
    }
    const trimmedComment = comment.trim();
    if (trimmedComment) {
      body.comment = trimmedComment;
    }
    if (!workflowId.startsWith('case-')) {
      return throwError(() => new Error(`Unsupported inbox item id: ${workflowId}`));
    }
    return this._http.post<void>(`${environment.apiUrl}/agentcase/${workflowId.slice(5)}/feedback`, body);
  }

  /**
   * Adds a comment to the workflow (exception). The author is stamped server-side from the
   * access token; in mock mode we fabricate one from the signed-in account so the UI updates.
   */
  addComment(workflowId: string, text: string): Observable<WorkflowComment> {
    const commentText = text.trim();
    if (environment.useMockData) {
      const account = this._authService.getAccount();
      const email = this._authService.getAccountEmail();
      return of({
        id: `mock-${Date.now()}`,
        text: commentText,
        author: account?.name || email || 'You',
        authorEmail: email || null,
        createdAt: new Date().toISOString(),
      });
    }
    if (!workflowId.startsWith('case-')) {
      return throwError(() => new Error(`Unsupported inbox item id: ${workflowId}`));
    }
    return this._http
      .post<WorkflowCommentDto>(`${environment.apiUrl}/agentcase/${workflowId.slice(5)}/comments`, { commentText })
      .pipe(map(dto => this.mapComment(dto)));
  }

  private mapComment(dto: WorkflowCommentDto): WorkflowComment {
    return {
      id: String(dto.agentCaseCommentId),
      text: dto.commentText,
      author: dto.createdBy,
      authorEmail: dto.createdByEmail,
      createdAt: dto.createdDate ?? new Date().toISOString(),
    };
  }
}
